"use client";

import dynamic from "next/dynamic";

import { APP_URL } from "@/config/urls";

import { PRIMARY_BUTTON_CLASSNAME } from "./primitives";

/**
 * The outcome screen shown after Atom redirects back — ported from the source's
 * `PaymentStatus`.
 *
 * The three illustrations are the source's own SVG components, copied verbatim
 * so the artwork matches. Two of them embed base64 rasters and together weigh
 * ~310KB, so each is loaded with `next/dynamic`: only the one matching the
 * actual outcome is ever downloaded, and none of it touches the initial bundle.
 */

const Success = dynamic(() => import("./statusIcons/Success"), { ssr: false });
const Failure = dynamic(() => import("./statusIcons/Failure"), { ssr: false });
const Pending = dynamic(() => import("./statusIcons/Pending"), { ssr: false });

interface PaymentStatusProps {
  /** "SUCCESS" | "FAILED" | "PENDING", as the service reports it. */
  status: string;
}

const TEXT_COLOR: Record<string, string> = {
  SUCCESS: "text-[#24AB74]",
  PENDING: "text-[#F0A727]",
};
/** Anything unrecognised reads as a failure, matching the source's fallthrough. */
const FAILED_TEXT_COLOR = "text-[#DF3639]";

const HEADLINE: Record<string, string> = {
  SUCCESS: "Registration Successful",
  FAILED: "Payment Failed",
  PENDING: "Payment Pending",
};

const DETAIL: Record<string, string> = {
  SUCCESS: "Internship platform credentials will be shared over email",
  FAILED: "Please try again using a different payment method",
  PENDING:
    "We are processing your payment, we'll notify to your email once the payment is processed.",
};

export default function PaymentStatus({ status }: PaymentStatusProps) {
  const textColor = TEXT_COLOR[status] || FAILED_TEXT_COLOR;
  const learnUrl = APP_URL.LEARN_URL?.trim();

  return (
    <div className="flex flex-col w-full justify-center items-center mt-10">
      {status === "SUCCESS" ? <Success /> : null}
      {status === "FAILED" ? <Failure /> : null}
      {status === "PENDING" ? <Pending /> : null}

      <p
        className={`${textColor} text-center text-[20px] not-italic font-semibold leading-[normal] mt-10`}
      >
        {HEADLINE[status] || ""}
      </p>

      <p className="text-[#1A1C1B] text-center text-[16px] not-italic font-normal leading-[normal] my-5">
        {DETAIL[status] || ""}
      </p>

      {/* Only offered on success, and only when the Learn URL is configured —
          the source renders a link with an undefined href otherwise. */}
      {status === "SUCCESS" && learnUrl ? (
        <a href={learnUrl} target="_blank" rel="noopener noreferrer">
          <button
            type="button"
            className={`${PRIMARY_BUTTON_CLASSNAME} !px-8 !py-3 text-sm tab:text-lg h-[42px]`}
          >
            Start Learning
          </button>
        </a>
      ) : null}
    </div>
  );
}
